import React from "react";
import { Modal } from "react-bootstrap";
import Resources from "../../../types/Resources";
import ChecklistRadioOptions from "../../Common/ChecklistRadioOptions";
import ExcavationAndPolingChecklistFormState from "../../../types/forms/Permits/Conditions/ExcavationAndPolingChecklistFormState";
import RadioButton from "../../Common/RadioButton";

type ExcavationAndPolingChecklistModalProps = {
    checklistVersion: number;
    formState: ExcavationAndPolingChecklistFormState;
    onChange: (fn: (prevValue: ExcavationAndPolingChecklistFormState) => ExcavationAndPolingChecklistFormState) => void;
    resources: Resources;
    show: boolean;
    onHide: () => void;
    canManageContent: boolean;
};

function ExcavationAndPolingChecklistModal({
    checklistVersion,
    formState,
    onChange,
    resources,
    show,
    onHide,
    canManageContent
}: ExcavationAndPolingChecklistModalProps) {
    function changeHandler(key: keyof ExcavationAndPolingChecklistFormState, value: any) {
        onChange((prevValue) => {
            const updatedFormState: ExcavationAndPolingChecklistFormState = {
                ...prevValue,
                [key]: { ...prevValue[key], value }
            };

            return updatedFormState;
        });
    }

    return (
        <Modal show={show} onHide={onHide} size="xl">
            <Modal.Header closeButton>
                <h1 className="h5">{resources.get("CheckListExcavation")}</h1>
            </Modal.Header>
            <Modal.Body>
                {checklistVersion === 0 ? (
                    <ChecklistVersion0
                        canManageContent={canManageContent}
                        changeHandler={changeHandler}
                        formState={formState}
                        resources={resources}
                    />
                ) : (
                    <ChecklistVersion202602
                        canManageContent={canManageContent}
                        changeHandler={changeHandler}
                        formState={formState}
                        resources={resources}
                    />
                )}
            </Modal.Body>
        </Modal>
    );
}

type ChecklistProps = {
    resources: Resources;
    formState: ExcavationAndPolingChecklistFormState;
    canManageContent: boolean;
    changeHandler: (key: keyof ExcavationAndPolingChecklistFormState, value: any) => void;
};

function ChecklistVersion0({ resources, formState, changeHandler, canManageContent }: ChecklistProps) {
    return (
        <ul className="list-unstyled">
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-submitted-to-county-board"
                    labelText={resources.get("IsSubmittedToCountyBoard")}
                    onChange={(updatedValue) => changeHandler("isSubmittedToCountyBoard", updatedValue)}
                    resources={resources}
                    value={formState.isSubmittedToCountyBoard.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <div className="d-flex justify-content-between form-group">
                    <div>{resources.get("HowIsAreaBlockedOff")}</div>
                    <div className="d-flex">
                        <RadioButton
                            checked={formState.howIsAreaBlockedOff.value === "ClosedAreaTape"}
                            onSelect={() => changeHandler("howIsAreaBlockedOff", "ClosedAreaTape")}
                            label={resources.get("ClosedAreaTape")}
                            readOnly={!canManageContent}
                                    inputId="how-is-area-blocked-off__closed-area-tape"
                            name="how-is-area-blocked-off"
                            containerClassName="me-3"
                        />

                        <RadioButton
                            checked={formState.howIsAreaBlockedOff.value === "ConesEtc"}
                            onSelect={() => changeHandler("howIsAreaBlockedOff", "ConesEtc")}
                            label={resources.get("ConesEtc")}
                            readOnly={!canManageContent}
                            inputId="how-is-area-blocked-off__cones-etc"
                            name="how-is-area-blocked-off"
                            containerClassName="me-3"
                        />

                        <RadioButton
                            checked={formState.howIsAreaBlockedOff.value === "notApplicable"}
                            onSelect={() => changeHandler("howIsAreaBlockedOff", "notApplicable")}
                            label={resources.get("NotApplicable")}
                            readOnly={!canManageContent}
                            inputId="how-is-area-blocked-off__not-applicable"
                            name="how-is-area-blocked-off"
                        />
                    </div>
                </div>
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-tests-done-for-lab"
                    labelText={resources.get("IsTestsDoneForLab")}
                    onChange={(updatedValue) => changeHandler("isTestsDoneForLab", updatedValue)}
                    resources={resources}
                    value={formState.isTestsDoneForLab.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-release-done"
                    labelText={resources.get("IsReleaseDone")}
                    onChange={(updatedValue) => changeHandler("isReleaseDone", updatedValue)}
                    resources={resources}
                    value={formState.isReleaseDone.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-cables-in-the-ground"
                    labelText={resources.get("IsCablesInTheGround")}
                    onChange={(updatedValue) => changeHandler("isCablesInTheGround", updatedValue)}
                    resources={resources}
                    value={formState.isCablesInTheGround.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-product-pipes-or-the-like-in-the-ground"
                    labelText={resources.get("IsProductPipesOrTheLikeInTheGround")}
                    onChange={(updatedValue) => changeHandler("isProductPipesOrTheLikeInTheGround", updatedValue)}
                    resources={resources}
                    value={formState.isProductPipesOrTheLikeInTheGround.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-deeper-than-120-centimeters"
                    labelText={resources.get("IsDepperThan120Centimeters")}
                    onChange={(updatedValue) => changeHandler("isDepperThan120Centimeters", updatedValue)}
                    resources={resources}
                    value={formState.isDepperThan120Centimeters.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-risk-of-race"
                    labelText={resources.get("IsRiskOfRace")}
                    onChange={(updatedValue) => changeHandler("isRiskOfRace", updatedValue)}
                    resources={resources}
                    value={formState.isRiskOfRace.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="can-safley-descend-shaft"
                    labelText={resources.get("CanSafleyDecendShaft")}
                    onChange={(updatedValue) => changeHandler("canSafleyDecendShaft", updatedValue)}
                    resources={resources}
                    value={formState.canSafleyDecendShaft.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-gas-part-of-the-test"
                    labelText={resources.get("IsGasPartOfTheTest")}
                    onChange={(updatedValue) => changeHandler("isGasPartOfTheTest", updatedValue)}
                    resources={resources}
                    value={formState.isGasPartOfTheTest.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-manhole-guard-available"
                    labelText={resources.get("IsManHoleGuardAvailable")}
                    onChange={(updatedValue) => changeHandler("isManHoleGuardAvailable", updatedValue)}
                    resources={resources}
                    value={formState.isManHoleGuardAvailable.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-masses-oil-contaminated"
                    labelText={resources.get("IsMassesOilContanimated")}
                    onChange={(updatedValue) => changeHandler("isMassesOilContanimated", updatedValue)}
                    resources={resources}
                    value={formState.isMassesOilContanimated.value}
                    readOnly={!canManageContent}
                />
            </li>
        </ul>
    );
}

function ChecklistVersion202602({ resources, formState, changeHandler, canManageContent }: ChecklistProps) {
    return (
        <ul className="list-unstyled">
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-submitted-to-county-board-for-excavation"
                    labelText={resources.get("IsSubmittedToCountyBoardForExcavation")}
                    onChange={(updatedValue) => changeHandler("isSubmittedToCountyBoardForExcavation", updatedValue)}
                    resources={resources}
                    value={formState.isSubmittedToCountyBoardForExcavation.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <div className="d-flex justify-content-between form-group">
                    <div>{resources.get("HowIsAreaBlockedOff")}</div>
                    <div className="d-flex">
                        <RadioButton
                            checked={formState.howIsAreaBlockedOff.value === "ClosedAreaTape"}
                            onSelect={() => changeHandler("howIsAreaBlockedOff", "ClosedAreaTape")}
                            label={resources.get("ClosedAreaTape")}
                            readOnly={!canManageContent}
                            inputId="how-is-area-blocked-off__closed-area-tape"
                            name="how-is-area-blocked-off"
                            containerClassName="me-3"
                        />

                        <RadioButton
                            checked={formState.howIsAreaBlockedOff.value === "ConesEtc"}
                            onSelect={() => changeHandler("howIsAreaBlockedOff", "ConesEtc")}
                            label={resources.get("ConesEtc")}
                            readOnly={!canManageContent}
                            inputId="how-is-area-blocked-off__cones-etc"
                            name="how-is-area-blocked-off"
                            containerClassName="me-3"
                        />

                        <RadioButton
                            checked={formState.howIsAreaBlockedOff.value === "notApplicable"}
                            onSelect={() => changeHandler("howIsAreaBlockedOff", "notApplicable")}
                            label={resources.get("NotApplicable")}
                            readOnly={!canManageContent}
                            inputId="how-is-area-blocked-off__not-applicable"
                            name="how-is-area-blocked-off"
                        />
                    </div>
                </div>
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-tests-done-for-lab"
                    labelText={resources.get("IsTestsDoneForLab")}
                    onChange={(updatedValue) => changeHandler("isTestsDoneForLab", updatedValue)}
                    resources={resources}
                    value={formState.isTestsDoneForLab.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-masses-oil-contaminated"
                    labelText={resources.get("IsMassesOilContanimated")}
                    onChange={(updatedValue) => changeHandler("isMassesOilContanimated", updatedValue)}
                    resources={resources}
                    value={formState.isMassesOilContanimated.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-release-done"
                    labelText={resources.get("IsReleaseDone")}
                    onChange={(updatedValue) => changeHandler("isReleaseDone", updatedValue)}
                    resources={resources}
                    value={formState.isReleaseDone.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-cables-in-the-ground"
                    labelText={resources.get("IsCablesInTheGround")}
                    onChange={(updatedValue) => changeHandler("isCablesInTheGround", updatedValue)}
                    resources={resources}
                    value={formState.isCablesInTheGround.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-product-pipes-or-the-like-in-the-ground"
                    labelText={resources.get("IsProductPipesOrTheLikeInTheGround")}
                    onChange={(updatedValue) => changeHandler("isProductPipesOrTheLikeInTheGround", updatedValue)}
                    resources={resources}
                    value={formState.isProductPipesOrTheLikeInTheGround.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-deeper-than-120-centimeters"
                    labelText={resources.get("IsDepperThan120Centimeters")}
                    onChange={(updatedValue) => changeHandler("isDepperThan120Centimeters", updatedValue)}
                    resources={resources}
                    value={formState.isDepperThan120Centimeters.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-risk-of-race"
                    labelText={resources.get("IsRiskOfRace")}
                    onChange={(updatedValue) => changeHandler("isRiskOfRace", updatedValue)}
                    resources={resources}
                    value={formState.isRiskOfRace.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="can-safley-descend-shaft"
                    labelText={resources.get("CanSafleyDecendShaft")}
                    onChange={(updatedValue) => changeHandler("canSafleyDecendShaft", updatedValue)}
                    resources={resources}
                    value={formState.canSafleyDecendShaft.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-gas-tested-in-hole-before-entry"
                    labelText={resources.get("IsGasTestedInHoleBeforeEntry")}
                    onChange={(updatedValue) => changeHandler("isGasTestedInHoleBeforeEntry", updatedValue)}
                    resources={resources}
                    value={formState.isGasTestedInHoleBeforeEntry.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-trained-excavation-guard-available"
                    labelText={resources.get("IsTrainedExcavationGuardAvailable")}
                    onChange={(updatedValue) => changeHandler("isTrainedExcavationGuardAvailable", updatedValue)}
                    resources={resources}
                    value={formState.isTrainedExcavationGuardAvailable.value}
                    readOnly={!canManageContent}
                />
            </li>
        </ul>
    );
}

export default ExcavationAndPolingChecklistModal;
